/**
 * Which offline cache Firestore is opened with, and why Safari gets a
 * different one.
 *
 * ── The bug ───────────────────────────────────────────────────────────────
 *
 * `persistentMultipleTabManager()` elects a primary tab by taking a lease in
 * IndexedDB; every other tab waits for the holder to release it. In Safari
 * that negotiation does not complete. It does not throw and it does not time
 * out — the tab simply waits, so every `onSnapshot` stays silent forever and
 * the screen sits on "Loading your trips…" with an empty console. A
 * `try/catch` around `initializeFirestore` cannot help: there is no error.
 *
 * Trey, 24 Aug 2026, after hours spent blaming the staging environment: the
 * same URLs "work fine on Chrome but not safari" — including a PRODUCTION
 * one. That single sentence solved it. It was never an environment problem.
 * It was every Safari user, on both programs, for as long as the multi-tab
 * manager has been unconditional.
 *
 * ── The trade ─────────────────────────────────────────────────────────────
 *
 * Chrome and Firefox keep multi-tab and need it: an advisor really does have
 * `/app` and `/v2` open at once, and the single-tab manager rejects the
 * second tab with `failed-precondition`. Safari gets NO browser-side store at
 * all — see `cacheSettings` for why single-tab was not enough.
 *
 * ⚠ Do not make this unconditional again without re-testing in WebKit. The
 * regression is silent: a dashboard that never leaves its loading state.
 */

import { memoryLocalCache, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore'

/**
 * WebKit-as-Safari, rather than a Chromium wearing `AppleWebKit` in its
 * user-agent. Chrome, Edge and Opera all claim `AppleWebKit`; only Safari
 * claims it without naming an engine of its own.
 *
 * A user-agent test rather than a feature test, deliberately: what is being
 * detected is a BUG, and a bug has no capability to probe for. The failure is
 * silent, so there is nothing to attempt and recover from either.
 *
 * @param {string} [ua]
 * @returns {boolean}
 */
export function isSafari(ua) {
  const agent = typeof ua === 'string' ? ua : (globalThis.navigator && globalThis.navigator.userAgent) || ''
  return /AppleWebKit/.test(agent) && /Safari/.test(agent) && !/Chrome|Chromium|Edg|OPR|Android/.test(agent)
}

/**
 * The `localCache` option for `initializeFirestore`.
 *
 * @param {string} [ua] - for tests
 * @returns {any}
 */
export function cacheSettings(ua) {
  /* Safari gets NO IndexedDB at all, not merely a different tab manager.
     The first attempt at this fix kept persistence and only dropped the
     lease; Trey reported it still broken, which rules out the lease as the
     whole story and puts the browser's IndexedDB layer itself in the frame.
     Firestore opens that store asynchronously, so a failure there never
     reaches the `try/catch` around `initializeFirestore` — it just leaves
     every listener silent, which is the symptom.

     `memoryLocalCache` removes the entire class of problem: nothing is
     stored, nothing is leased, nothing can fail to open. The cost is that a
     Safari advisor re-reads from the network on each load and has no offline
     copy. That is a real downgrade and worth it against a screen that never
     loads — and it applies only to Safari. */
  if (isSafari(ua)) return memoryLocalCache()
  return persistentLocalCache({ tabManager: persistentMultipleTabManager() })
}
